import { useQuery } from '@tanstack/react-query';
import { api } from '@/shared/api/axios';
import type {
  BriefingInsight,
  CallOptimizerResponse,
  LeadScore,
  NicheRow,
  AnomalyRow,
  ScrapeAdvisorResponse,
} from '../types';

// Deal scores reuse the EXISTING canonical Deals cache (['deals','ai-scores']).
// Re-exported here so the AI namespace exposes it without a second request.
export { useDealScores } from '@/features/deals/hooks/useDeals';
// Deals list cache reused for joining titles/stages to scores.
export { useDeals } from '@/features/deals/hooks/useDeals';

export function useBriefing() {
  return useQuery({
    queryKey: ['ai', 'briefing'],
    queryFn: async () => {
      const { data } = await api.get<{ insights: BriefingInsight[] }>('/api/ai/briefing');
      return data.insights ?? [];
    },
    staleTime: 5 * 60 * 1000,
  });
}

export function useLeadScores() {
  return useQuery({
    queryKey: ['ai', 'lead-scores'],
    queryFn: async () => {
      const { data } = await api.get<{ scores: LeadScore[] }>('/api/ai/lead-scores');
      return data.scores ?? [];
    },
    staleTime: 5 * 60 * 1000,
  });
}

export function useCallOptimizer() {
  return useQuery({
    queryKey: ['ai', 'call-optimizer'],
    queryFn: async () => {
      const { data } = await api.get<CallOptimizerResponse>('/api/ai/call-optimizer');
      return { bestWindows: data.bestWindows ?? [], hourlyData: data.hourlyData ?? [] };
    },
    staleTime: 5 * 60 * 1000,
  });
}

export function useNicheIntelligence() {
  return useQuery({
    queryKey: ['ai', 'niche-intelligence'],
    queryFn: async () => {
      const { data } = await api.get<{ niches: NicheRow[] }>('/api/ai/niche-intelligence');
      return data.niches ?? [];
    },
    staleTime: 5 * 60 * 1000,
  });
}

export function useAnomalies() {
  return useQuery({
    queryKey: ['ai', 'anomalies'],
    queryFn: async () => {
      const { data } = await api.get<{ anomalies: AnomalyRow[] }>('/api/ai/anomalies');
      return data.anomalies ?? [];
    },
    staleTime: 5 * 60 * 1000,
  });
}

export function useScrapeAdvisor() {
  return useQuery({
    queryKey: ['ai', 'scrape-advisor'],
    queryFn: async () => {
      const { data } = await api.get<ScrapeAdvisorResponse>('/api/ai/scrape-advisor');
      return { suggestedNiches: data.suggestedNiches ?? [], topLocations: data.topLocations ?? [] };
    },
    staleTime: 5 * 60 * 1000,
  });
}
